/**
 * Everything OrderHub-specific in this package lives here — the only file to
 * edit when lifting the package into another repo.
 *
 * Runtime-neutral: imported by Convex (via `./server`), bun e2e, browsers and
 * React Native, so no React or Amplify imports. The Convex CLI bundles
 * `auth.config.ts` (and everything it imports) as one esbuild module evaluated
 * at push, so this file DEEP-IMPORTS from `@packages/shared` instead of its
 * barrel — the barrel would drag the whole schema tree into that bundle (same
 * precedent as packages/backend/convex/convex.config.ts).
 */

import { type STAGES, zStage } from '@packages/shared/src/schemas/enums';
import { convertToIntFormat, isMobileNumberValid } from '@packages/shared/src/utils/phone';
import { makeFunctionReference } from 'convex/server';
import * as COGNITO_POOLS from './cognito.json';

//
// Dev auth bypass identifiers
//

/** `iss` claim of every dev bypass token. Never a real OIDC endpoint — the JWKS ships inline with the provider. */
export const DEV_AUTH_ISSUER = 'https://dev-auth.orderhub.local';

/** `aud` claim of every dev bypass token, and the Convex provider's `applicationID`. */
export const DEV_AUTH_APPLICATION_ID = 'orderhub-dev-auth';

/** Key id shared by the JWKS entry and every signed token header. */
export const DEV_AUTH_KID = 'dev-auth-1';

/** localStorage key a frontend stores its dev bypass token under. */
export const DEV_AUTH_TOKEN_KEY = 'orderhub:dev-auth-token';

/**
 * Roles the backend's `devAuth.login` action accepts. Single source of truth:
 * the action derives its `role` arg from this (`z.literal(DEV_LOGIN_ROLES)`).
 */
export const DEV_LOGIN_ROLES = ['superAdmin', 'restaurantUser', 'restaurantCustomer'] as const;
export type DevLoginRole = (typeof DEV_LOGIN_ROLES)[number];

/**
 * String reference to the backend's login action, so this package never depends
 * on `@packages/backend`. packages/backend/convex/devAuth.ts asserts its args
 * match, so a renamed or re-typed action fails typecheck instead of at runtime.
 */
export const devLogin = makeFunctionReference<'action', { role: DevLoginRole }, string>(
  'devAuth:login',
);

//
// Cognito username scheme
//

/**
 * The Cognito username for an AU mobile number: `+614xxxxxxxx`, prefixed with
 * `${scope}_` when a scope is given (consumer-web scopes customers per
 * restaurant with `restaurant.uuid`). The error message is user-visible.
 */
export const toCognitoUsername = (mobile: string, scope?: string): string => {
  if (!isMobileNumberValid(mobile)) {
    throw new Error('Invalid mobile number.\nPlease use format 04xxxxxxxx.');
  }
  const intMobile = convertToIntFormat(mobile);
  return scope ? `${scope}_${intMobile}` : intMobile;
};

//
// Cognito user pools
//

/**
 * The identity provider Square sellers sign in with, named the same in every pool: OrderHub's
 * OpenID Connect facade over Square's OAuth (packages/backend/convex/integrations/square/oidc.ts).
 */
export const SQUARE_IDENTITY_PROVIDER_NAME = 'Square';

/** Pool ids per stage; e2e reads `.dev` to validate stored Amplify sessions. */
export const cognitoPools: Record<(typeof STAGES)[number], typeof COGNITO_POOLS.dev> =
  COGNITO_POOLS;

/**
 * Convex throws an error if ENV variables are tried to be used in any imported files.
 * See https://discord.com/channels/1019350475847499849/1252573078848798721/1252574564479668236
 * To work around this, we wrap this logic in a function.
 *
 * `oauth` turns on signing in through the pool's hosted UI (`signInWithRedirect`), which sends
 * the user back to `redirectUrl`, also where signing out lands. Both must be callback and
 * sign-out URLs of the pool's app client.
 */
export const getCognitoConfig = (stage: string, oauth?: { redirectUrl: string }) => {
  const parsed = zStage.safeParse(stage);
  if (!parsed.success) {
    throw new Error(`Unrecognized STAGE value: ${stage}`);
  }

  console.log(`[auth] stage: ${parsed.data}`);

  const { userPoolId, userPoolClientId, oauthDomain } = cognitoPools[parsed.data];

  return {
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        ...(oauth && {
          loginWith: {
            oauth: {
              domain: oauthDomain,
              scopes: ['openid'],
              redirectSignIn: [oauth.redirectUrl],
              redirectSignOut: [oauth.redirectUrl],
              responseType: 'code' as const,
            },
          },
        }),
      },
    },
  };
};
